import React, { useEffect } from 'react';
import { useAuth } from '@/lib/AuthContext';
import { base44 } from '@/api/base44Client';
import { saveFCMToken } from '@/lib/fcm';
import NotificationCenter from '@/components/NotificationCenter';
import FloatingNotifications from '@/components/FloatingNotifications';
import InstallPrompt from '@/components/InstallPrompt';
import { registerServiceWorker, getFCMToken, setupForegroundMessageListener } from '@/components/firebaseConfig';
import { queryClientInstance } from '@/lib/query-client';

function LayoutContent({ children, currentPageName }) {
  const { user } = useAuth();

  useEffect(() => {
    let cancelled = false;

    const initializeNotifications = async () => {
      try {
        // Guard SSR
        if (typeof window === 'undefined') return;
        if (!('Notification' in window)) return;

        // 1) Register SW
        const swRegistration = await registerServiceWorker();
        if (!swRegistration) return;

        // 2) Foreground listener
        await setupForegroundMessageListener((payload) => {
          console.log('Notification received in foreground:', payload);
          const tag = payload?.data?.tag || '';
          if (tag.includes('duel_') && (tag.includes('_win') || tag.includes('_completed'))) {
            queryClientInstance.invalidateQueries({ queryKey: ['userRecentVictories'] });
            queryClientInstance.invalidateQueries({ queryKey: ['duel'] });
            queryClientInstance.invalidateQueries({ queryKey: ['playgrounds'] });
          }
        });

        // 3) Token only if permission granted
        if (Notification.permission !== 'granted') {
          console.log('Notification permission:', Notification.permission);
          return;
        }

        const fcmToken = await getFCMToken(swRegistration);
        if (cancelled) return;

        if (!fcmToken) {
          console.warn('⚠️ Aucun token FCM généré');
          return;
        }

        // 4) Save token server-side
        await saveFCMToken(fcmToken);
        console.log('✅ FCM token sauvegardé');
      } catch (err) {
        console.error('Échec de l\'initialisation des notifications:', err);
      }
    };

    initializeNotifications();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  return (
    <>
      {user && <NotificationCenter user={user} />}
      {user && (
        <FloatingNotifications
          user={user}
          onPlaygroundSelect={currentPageName === 'Home' ? window.__onPlaygroundSelect : null}
        />
      )}

      <InstallPrompt />

      <div
        className="bg-[#0B0D12] text-white min-h-screen flex flex-col"
        style={{
          '--violet': '#6E2BFF',
          '--dark-card': '#13151D',
          '--text-primary': '#FFFFFF',
          '--text-secondary': '#9CA0AB',
          '--bg-main': '#0B0D12',
          '--success': '#3EDC81',
          '--error': '#FF5A5A',
          paddingTop: 'env(safe-area-inset-top)'
        }}
      >
        <style>{`
          :root {
            --violet: #6E2BFF;
            --dark-card: #13151D;
            --text-primary: #FFFFFF;
            --text-secondary: #9CA0AB;
            --bg-main: #0B0D12;
            --success: #3EDC81;
            --error: #FF5A5A;
          }
          body { 
            background-color: #0B0D12; 
            color: #FFFFFF; 
            overscroll-behavior: none;
            -webkit-overflow-scrolling: touch;
          }
          button:not([disabled]), a, [role="button"], select, input[type="button"], input[type="submit"] { 
            transition: all 0.2s ease;
            user-select: none;
            -webkit-user-select: none;
            -webkit-tap-highlight-color: transparent;
          }
          .mapboxgl-ctrl-logo, .mapboxgl-ctrl-attrib, .mapboxgl-ctrl-attrib-button {
            display: none !important;
          }
        `}</style>

        <div className="flex-1">{children}</div>
      </div>
    </>
  );
}

export default function Layout({ children, currentPageName }) {
  return (
    <LayoutContent currentPageName={currentPageName}>
      {children}
    </LayoutContent>
  );
}